import { isWeb } from 'utils/responsiveness';

import {
    deletePlayerSubtitlesFontSize,
    deleteProfilePlayerAudio,
    deleteProfilePlayerSubtitles,
    setModalPlayerFullscreen,
    setMuted,
    setPlayer,
    setPlayerAudio,
    setPlayerSubtitles,
    setPlayerSubtitlesFontSize,
    setShouldUpdateId,
    setSpotlightVideoVisible,
    setVolume,
    showPlayer,
} from './actions';
import {
    DELETE_PLAYER_SUBTITLES_FONT_SIZE,
    DELETE_PROFILE_PLAYER_AUDIO,
    DELETE_PROFILE_PLAYER_SUBTITLES,
    HIDE_PLAYER,
    SET_GENERATE_ID,
    SET_MODAL_PLAYER_FULLSCREEN,
    SET_MUTED,
    SET_PLAYER,
    SET_PLAYER_AUDIO,
    SET_PLAYER_SUBTITLES,
    SET_PLAYER_SUBTITLES_FONT_SIZE,
    SET_SPOTLIGHT_VIDEO_VISIBLE,
    SET_VOLUME,
    SHOW_PLAYER,
} from './constants';
import { PlayerState } from './reducer.types';

const initialState: PlayerState = {
    visible: false,
    videoItem: undefined,
    playbackType: undefined,
    fullscreenBlocked: false,
    railId: undefined,
    templateId: undefined,
    subtitles: {},
    audio: {},
    subtitlesFontSize: {},
    shouldUpdateId: true,
    fullscreen: !isWeb,
    spotlightVideoVisible: false,
    muted: false,
    volume: 1,
    prevPositiveVolume: 1,
};

const handlers = {
    [SHOW_PLAYER](
        state: PlayerState,
        { payload: { config } }: ReturnType<typeof showPlayer>,
    ): PlayerState {
        return {
            ...state,
            visible: true,
            videoItem: config.videoItem,
            playbackType: config.playbackType,
            railId: config.railId,
            templateId: config.templateId,
        };
    },
    [HIDE_PLAYER](state: PlayerState): PlayerState {
        return {
            ...state,
            visible: false,
        };
    },
    [SET_PLAYER](
        state: PlayerState,
        { payload: { config } }: ReturnType<typeof setPlayer>,
    ): PlayerState {
        return {
            ...state,
            videoItem: config.videoItem,
            playbackType: config.playbackType,
        };
    },

    [SET_PLAYER_SUBTITLES](
        state: PlayerState,
        { payload: { id, subtitles } }: ReturnType<typeof setPlayerSubtitles>,
    ): PlayerState {
        return {
            ...state,
            subtitles: { ...state.subtitles, [id]: subtitles },
        };
    },

    [DELETE_PROFILE_PLAYER_SUBTITLES](
        state: PlayerState,
        { payload: { id } }: ReturnType<typeof deleteProfilePlayerSubtitles>,
    ): PlayerState {
        const updatedSubtitles = { ...state.subtitles };
        if (updatedSubtitles[id]) {
            delete updatedSubtitles[id];
        }

        return {
            ...state,
            subtitles: { ...updatedSubtitles },
        };
    },

    [SET_PLAYER_AUDIO](
        state: PlayerState,
        { payload: { id, audio } }: ReturnType<typeof setPlayerAudio>,
    ): PlayerState {
        return {
            ...state,
            audio: { ...state.audio, [id]: audio },
        };
    },

    [DELETE_PROFILE_PLAYER_AUDIO](
        state: PlayerState,
        { payload: { id } }: ReturnType<typeof deleteProfilePlayerAudio>,
    ): PlayerState {
        const updatedAudio = { ...state.audio };
        if (updatedAudio[id]) {
            delete updatedAudio[id];
        }

        return {
            ...state,
            audio: { ...updatedAudio },
        };
    },

    [SET_PLAYER_SUBTITLES_FONT_SIZE](
        state: PlayerState,
        {
            payload: { profileId, fontSize },
        }: ReturnType<typeof setPlayerSubtitlesFontSize>,
    ): PlayerState {
        return {
            ...state,
            subtitlesFontSize: {
                ...state.subtitlesFontSize,
                [profileId]: fontSize,
            },
        };
    },

    [DELETE_PLAYER_SUBTITLES_FONT_SIZE](
        state: PlayerState,
        {
            payload: { profileId },
        }: ReturnType<typeof deletePlayerSubtitlesFontSize>,
    ): PlayerState {
        const updatedSubtitlesFontSize = { ...state.subtitlesFontSize };

        if (updatedSubtitlesFontSize[profileId]) {
            delete updatedSubtitlesFontSize[profileId];
        }

        return {
            ...state,
            subtitlesFontSize: updatedSubtitlesFontSize,
        };
    },

    [SET_GENERATE_ID](
        state: PlayerState,
        { payload: { shouldUpdateId } }: ReturnType<typeof setShouldUpdateId>,
    ): PlayerState {
        return {
            ...state,
            shouldUpdateId,
        };
    },

    [SET_MODAL_PLAYER_FULLSCREEN](
        state: PlayerState,
        {
            payload: { fullscreen },
        }: ReturnType<typeof setModalPlayerFullscreen>,
    ): PlayerState {
        return {
            ...state,
            fullscreen,
        };
    },

    [SET_SPOTLIGHT_VIDEO_VISIBLE](
        state: PlayerState,
        {
            payload: { spotlightVideoVisible },
        }: ReturnType<typeof setSpotlightVideoVisible>,
    ): PlayerState {
        return {
            ...state,
            spotlightVideoVisible,
        };
    },

    [SET_MUTED](
        state: PlayerState,
        { payload: { muted } }: ReturnType<typeof setMuted>,
    ): PlayerState {
        const _muted = muted ?? !state.muted;

        return {
            ...state,
            muted: _muted,
            volume: _muted ? 0 : state.prevPositiveVolume,
        };
    },

    [SET_VOLUME](
        state: PlayerState,
        { payload: { volume } }: ReturnType<typeof setVolume>,
    ): PlayerState {
        return {
            ...state,
            volume,
            muted: volume === 0,
            prevPositiveVolume: volume || state.prevPositiveVolume,
        };
    },
};

export default { initialState, handlers };
